import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { addMonths, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth } from "date-fns";
import { ArrowRight, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { api, fmtTime, fmtDate, track } from "@/lib/api";

const STATUS = {
  available: { dot: "bg-green-700", text: "text-green-800", label: "Available" },
  partial: { dot: "bg-amber-600", text: "text-amber-700", label: "Partially available" },
  unavailable: { dot: "bg-red-700", text: "text-red-700", label: "Unavailable" },
  closed: { dot: "bg-stone-300", text: "text-stone-400", label: "Closed" },
  past: { dot: "bg-stone-200", text: "text-stone-300", label: "Past" },
};

export default function Availability() {
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(() => format(today, "yyyy-MM"));
  const [selected, setSelected] = useState(null);

  useEffect(() => { track("availability_opened"); }, []);

  const monthQ = useQuery({
    queryKey: ["availability", month],
    queryFn: () => api.get("/public/availability", { params: { month } }).then((r) => r.data),
    retry: 1,
  });

  const dayQ = useQuery({
    queryKey: ["availability-day", selected],
    queryFn: () => api.get("/public/availability/day", { params: { date: selected } }).then((r) => r.data),
    enabled: !!selected,
    retry: 1,
  });

  const monthDate = useMemo(() => new Date(`${month}-15T12:00:00`), [month]);
  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(monthDate)),
    end: endOfWeek(endOfMonth(monthDate)),
  }), [monthDate]);

  const canPrev = format(addMonths(monthDate, -1), "yyyy-MM") >= format(today, "yyyy-MM");
  const selectedStatus = selected ? (monthQ.data?.days?.[selected] || dayQ.data?.status) : null;

  return (
    <div className="bg-bone">
      <Seo title="Availability — The Owl's Nest" description="Check open dates and hours for private events at The Owl's Nest. Availability is subject to confirmation." />
      <Nav />
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-8 sm:pt-40 lg:px-16">
        <Reveal>
          <p className="overline-label">Availability</p>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Find your date.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-600">
            Availability is subject to confirmation — an inquiry does not hold a date.
            Booked times are shown without any private event details.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="card p-5 sm:p-8" data-testid="availability-calendar-view">
              <div className="flex items-center justify-between">
                <button onClick={() => canPrev && setMonth(format(addMonths(monthDate, -1), "yyyy-MM"))}
                  disabled={!canPrev} aria-label="Previous month" data-testid="availability-month-prev"
                  className="rounded-full p-2.5 text-stone-500 transition-colors hover:bg-bone2 disabled:opacity-30">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <p className="font-serif text-xl text-ink sm:text-2xl" data-testid="availability-month-picker">
                  {format(monthDate, "MMMM yyyy")}
                </p>
                <button onClick={() => setMonth(format(addMonths(monthDate, 1), "yyyy-MM"))}
                  aria-label="Next month" data-testid="availability-month-next"
                  className="rounded-full p-2.5 text-stone-500 transition-colors hover:bg-bone2">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-6 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d} className="py-2">{d}</span>)}
              </div>
              {monthQ.isLoading ? (
                <div className="flex h-72 items-center justify-center text-stone-400">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : monthQ.isError ? (
                <p className="flex h-72 items-center justify-center text-sm text-stone-500" data-testid="availability-error">
                  Availability temporarily unavailable.
                </p>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {days.map((d) => {
                    const iso = format(d, "yyyy-MM-dd");
                    const status = monthQ.data?.days?.[iso];
                    const inMonth = isSameMonth(d, monthDate);
                    const st = STATUS[status] || STATUS.past;
                    const selectable = inMonth && (status === "available" || status === "partial");
                    return (
                      <button key={iso} disabled={!selectable}
                        onClick={() => setSelected(iso)}
                        data-testid={`availability-day-${iso}`}
                        aria-label={`${fmtDate(iso)} — ${st.label}`}
                        className={`flex min-h-[3.4rem] flex-col items-center justify-center gap-1 rounded-xl text-sm transition-all sm:min-h-[4rem]
                          ${!inMonth ? "invisible" : ""}
                          ${selected === iso ? "bg-ink text-bone" : selectable ? "hover:bg-bone2 text-stone-700" : "text-stone-300"}`}>
                        <span className="font-medium">{format(d, "d")}</span>
                        <span className={`h-1.5 w-1.5 rounded-full ${selected === iso ? "bg-bone" : st.dot}`} />
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t border-black/5 pt-5">
                {["available", "partial", "unavailable"].map((k) => (
                  <span key={k} className="flex items-center gap-2 text-xs text-stone-500">
                    <span className={`h-2 w-2 rounded-full ${STATUS[k].dot}`} /> {STATUS[k].label}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="card sticky top-28 p-6 sm:p-8" data-testid="availability-day-agenda">
              {!selected ? (
                <div className="py-10 text-center">
                  <p className="font-serif text-2xl text-ink">Select a day</p>
                  <p className="mt-2 text-sm text-stone-500">Choose any green or amber day to see open hours.</p>
                </div>
              ) : dayQ.isLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
              ) : dayQ.isError ? (
                <p className="py-10 text-center text-sm text-stone-500">Availability temporarily unavailable.</p>
              ) : (
                <>
                  <p className="overline-label">{fmtDate(selected)}</p>
                  <p className={`mt-3 font-serif text-2xl ${STATUS[selectedStatus]?.text || "text-ink"}`}>
                    {STATUS[selectedStatus]?.label || "Unavailable"}
                  </p>
                  {dayQ.data?.windows?.length > 0 && (
                    <p className="mt-1 text-sm text-stone-500">
                      Rental hours: {dayQ.data.windows.map((w) => `${fmtTime(w.start)} – ${fmtTime(w.end)}`).join(" & ")}
                    </p>
                  )}
                  {dayQ.data?.free?.length > 0 && (
                    <div className="mt-6">
                      <p className="label">Open for events</p>
                      <div className="flex flex-wrap gap-2">
                        {dayQ.data.free.map((f) => (
                          <span key={f.start} className="rounded-full bg-green-50 px-4 py-2 text-xs font-semibold text-green-800 ring-1 ring-green-700/15">
                            {fmtTime(f.start)} – {fmtTime(f.end)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {dayQ.data?.busy?.length > 0 && (
                    <div className="mt-6">
                      <p className="label">Already booked</p>
                      <div className="space-y-2">
                        {dayQ.data.busy.map((b) => (
                          <div key={b.start} className="flex items-center justify-between rounded-xl bg-bone2 px-4 py-2.5 text-xs">
                            <span className="font-medium text-stone-700">{fmtTime(b.start)} – {fmtTime(b.end)}</span>
                            <span className="font-semibold uppercase tracking-wider text-red-700/80">Unavailable</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-6 text-xs leading-relaxed text-stone-400">
                    Events have a {(dayQ.data?.min_minutes || 120) / 60}-hour minimum, and every booking includes
                    a {dayQ.data?.buffer_minutes || 30}-minute buffer before and after for setup and cleanup.
                    Final confirmation happens with our office.
                  </p>
                  {(selectedStatus === "available" || selectedStatus === "partial") && (
                    <Link to={`/inquire?date=${selected}`} data-testid="availability-inquire-btn" className="btn-dark mt-6 w-full">
                      Inquire for {fmtDate(selected)} <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
